/**
 * @typedef {Object} HTTPResponse
 * @property {*} data - The response data (parsed JSON or text). Null if error occurred.
 * @property {Object|null} error - Error object if request failed. Null if successful.
 * @property {string} [error.message] - Error message
 * @property {number} [error.status] - HTTP status code (901 for malformed responses, original status for infrastructure errors)
 * @property {string} [error.statusText] - HTTP status text
 * @property {boolean} [error.isInfrastructureError] - True if error came from CDN/ALB (HTML response) or malformed JSON
 * @property {boolean} [error.isNetworkError] - True if network connectivity error (status 900)
 * @property {string} [error.body] - Raw error body (for infrastructure errors, null for malformed JSON)
 * @property {string} [error.parseError] - Parse error message when JSON parsing fails
 * @property {number} [error.originalStatus] - Original HTTP status code (for malformed responses with synthetic 901 status)
 * @property {Object} [error.originalError] - Original error when retries exhausted or unrecoverable error occurs
 * @property {number} [error.attempts] - Total number of attempts made (initial + retries) when max retries exceeded
 * @property {string} [error.errorType] - Error type classification (expected, retryable, auth, network, unrecoverable, unexpected)
 */

/**
 * @typedef {Object} HTTPClientOptions
 * @property {Function} [getToken] - Function that returns the current auth token. Required if refreshAuth is provided.
 * @property {Function} [refreshAuth] - Function that refreshes the auth token. Required if getToken is provided.
 * @property {number} [retries=3] - Number of retry attempts for retryable errors
 * @property {number} [backOffFactor=1000] - Base delay in milliseconds for exponential backoff (delay = 2^retries * backOffFactor)
 */

const ErrorType = Object.freeze({
	// Expected errors (don't report to Sentry)
	EXPECTED: 'expected',
	// Unexpected errors (report to Sentry)
	UNEXPECTED: 'unexpected',
	// Retryable errors
	RETRYABLE: 'retryable',
	// Auth errors
	AUTH: 'auth',
	// network errors
	NETWORK: 'network_error',
	// anything going wrong that we cant handle
	UNRECOVERABLE: 'unrecoverable',
});

const errorTypeMap = Object.freeze({
	// 2xx Success responses
	200: ErrorType.EXPECTED, // OK
	201: ErrorType.EXPECTED, // Created
	202: ErrorType.EXPECTED, // Accepted
	204: ErrorType.EXPECTED, // No Content

	// 3xx Redirection - usually handled by fetch, but good to be explicit
	301: ErrorType.EXPECTED, // Moved Permanently
	302: ErrorType.EXPECTED, // Found
	304: ErrorType.EXPECTED, // Not Modified

	// 4xx Client Errors
	400: ErrorType.EXPECTED, // Bad Request - client should handle validation errors
	401: ErrorType.AUTH, // Unauthorized - needs token refresh
	403: ErrorType.EXPECTED, // Forbidden - user doesn't have permission
	404: ErrorType.EXPECTED, // Not Found - resource doesn't exist
	405: ErrorType.EXPECTED, // Method Not Allowed
	406: ErrorType.EXPECTED, // Not Acceptable
	408: ErrorType.RETRYABLE, // Request Timeout
	409: ErrorType.EXPECTED, // Conflict - business logic error
	410: ErrorType.EXPECTED, // Gone - resource permanently deleted
	412: ErrorType.EXPECTED, // Precondition Failed
	413: ErrorType.EXPECTED, // Payload Too Large
	415: ErrorType.EXPECTED, // Unsupported Media Type
	418: ErrorType.UNEXPECTED, // I'm a teapot (should never happen in real APIs)
	422: ErrorType.EXPECTED, // Unprocessable Entity - validation errors
	429: ErrorType.RETRYABLE, // Too Many Requests - rate limiting

	// 5xx Server Errors
	500: ErrorType.RETRYABLE, // Internal Server Error - might be transient
	501: ErrorType.EXPECTED, // Not supported by server
	502: ErrorType.RETRYABLE, // Bad Gateway - proxy/load balancer issue
	503: ErrorType.RETRYABLE, // Service Unavailable - temporary overload
	504: ErrorType.RETRYABLE, // Gateway Timeout - upstream timeout
	507: ErrorType.RETRYABLE, // Insufficient Storage - might be temporary

	// Cloudflare-specific errors (infrastructure)
	520: ErrorType.RETRYABLE, // Unknown Error
	521: ErrorType.RETRYABLE, // Web Server Is Down
	522: ErrorType.RETRYABLE, // Connection Timed Out
	523: ErrorType.RETRYABLE, // Origin Is Unreachable
	524: ErrorType.RETRYABLE, // A Timeout Occurred
	525: ErrorType.RETRYABLE, // SSL Handshake Failed

	// Custom network error code
	900: ErrorType.NETWORK, // Network connectivity issues
	901: ErrorType.UNRECOVERABLE, // Unrecoverable error, malformed response etc.

	// Default fallback
	default: ErrorType.UNEXPECTED,
});

/**
 * Base HTTP Client that handles:
 * - Token injection
 * - Auth error handling
 * - Retry logic
 * - Response parsing
 * - Error categorization
 *
 */
export default class HTTPClient {
	/**
	 * Creates an HTTP client instance with optional authentication and retry logic.
	 *
	 * @param {string} baseUrl - The base URL of the API server (http:// or https://). Trailing slash will be removed.
	 * @param {HTTPClientOptions} [options={}] - Configuration options
	 * @throws {Error} If baseUrl is missing, invalid, or uses non-HTTP(S) protocol
	 * @throws {Error} If only one of getToken/refreshAuth is provided (both required together)
	 *
	 * @example
	 * // Unauthenticated client for public endpoints
	 * const publicClient = new HTTPClient('https://api.example.com');
	 *
	 * @example
	 * // Authenticated client with custom retry config
	 * const authClient = new HTTPClient('https://api.example.com', {
	 *   getToken: () => localStorage.getItem('token'),
	 *   refreshAuth: async () => { ... },
	 *   retries: 5,
	 *   backOffFactor: 2000
	 * });
	 */
	constructor(baseUrl, { getToken, refreshAuth, retries = 3, backOffFactor = 1000 } = {}) {
		if (!baseUrl) throw new Error('baseUrl is required');
		let url;
		try {
			url = new URL(baseUrl);
		} catch {
			throw new Error('baseUrl must be a valid URL');
		}

		if (!['http:', 'https:'].includes(url.protocol)) {
			throw new Error('baseUrl must use either the http or https protocol');
		}
		// Remove trailing slash for consistency
		this._baseUrl = baseUrl.replace(/\/$/, '');

		if (getToken && refreshAuth) {
			this._getToken = getToken;
			this._refreshAuth = refreshAuth;
		} else if (getToken && !refreshAuth) {
			throw new Error(
				'Missing function: refreshAuth function is required when getToken is set'
			);
		} else if (refreshAuth && !getToken) {
			throw new Error(
				'Missing function: getToken function is required when refreshAuth is set'
			);
		}

		this._retries = retries;
		this._backOffFactor = backOffFactor;
	}

	/**
	 * Builds a fetch request with headers and body.
	 * Injects auth token if available and stringifies body data.
	 *
	 * @private
	 * @param {string} url - Full URL to request
	 * @param {Object} [options={}] - Fetch options (method, body, headers)
	 * @returns {Function} Async function that executes the fetch request
	 */
	_buildRequest(url, options = {}) {
		const token = this._getToken ? this._getToken() : undefined;

		const headers = {
			'Content-Type': 'application/json',
			...(token && { Authorization: `Bearer ${token}` }),
			...options.headers,
		};

		const body = options.body ? JSON.stringify(options.body) : undefined;
		const request = async () => fetch(url, { ...options, headers, body });
		return request;
	}

	/**
	 * Handles response parsing and error classification.
	 * Differentiates between API errors (JSON), infrastructure errors (HTML/text),
	 * network errors (status 900), and malformed responses (status 901).
	 *
	 * @private
	 * @param {Response} response - Fetch API response object or synthetic error response
	 * @returns {Promise<{data: *, error: Object|null, errorType: string|null}>} Parsed response with error classification
	 */
	async _handleResponse(response) {
		if (response.status === 900) {
			return {
				data: null,
				error: {
					message: 'Network connectivity error',
					statusText: response.statusText,
					isNetworkError: true,
					originalError: response.error,
				},
				errorType: ErrorType.NETWORK,
			};
		}
		// priming the return values to prevent returning undefined.
		let data = null;
		let error = null;
		let errorType = null;

		const contentType = response.headers?.get('content-type');
		const isJson = contentType && contentType.includes('application/json');

		if (!response.ok) {
			errorType = errorTypeMap[response.status] || errorTypeMap.default;

			if (isJson) {

				try {
					error = await response.json();
				} catch (parseError) {
					errorType = ErrorType.UNRECOVERABLE;
					error = {
						message: 'Failed to parse error response',
						parseError: parseError.message,
						statusText: response.statusText,
						status: 901,
						errorType: ErrorType.UNRECOVERABLE,
						isInfrastructureError: true,
						body: null,
					};
				}
			} else {
				// Infrastructure error - CDN/ALB returned HTML or plain text
				const errorText = await response.text();
				error = {
					message: 'Infrastructure error',
					statusText: response.statusText,
					status: response.status,
					body: errorText,
					isInfrastructureError: true,
				};
			}
		} else {
			if (isJson) {
				try {
					data = await response.json();
				} catch (parseError) {
					return {
						data: null,
						error: {
							message: 'Failed to parse success response',
							parseError: parseError.message,
							status: 901,
							isInfrastructureError: true,
						},
						errorType: ErrorType.UNRECOVERABLE,
					};
				}
			} else {
				data = await response.text();
			}
		}

		// take care to maintain this pattern throughout on data and error return, as this is what is expected by the caller.
		return { data, error, errorType };
	}

	/**
	 * Core request handler with retry logic and error handling.
	 * Handles auth token refresh, retryable errors with exponential backoff,
	 * and error classification routing.
	 *
	 * @private
	 * @param {string} url - Full URL to request
	 * @param {Object} options - Fetch options
	 * @param {number} [retries=0] - Current retry attempt count
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 */
	async _makeRequest(url, options, retries = 0) {
		const request = this._buildRequest(url, options);

		const response = await request().catch(err => {
			return {
				ok: false,
				status: 900,
				statusText: 'Network connectivity issues',
				error: err,
			};
		});

		const { data, error, errorType } = await this._handleResponse(response);

		/**
		 * utility for retryable errors.
		 */
		const retry = async () => {
			if (retries >= this._retries) {
				const retryError = {
					message: 'Max retries exceeded',
					originalError: error,
					attempts: retries + 1,
					errorType,
				};
				return { data: null, error: retryError };
			}

			const delay = 2 ** retries * this._backOffFactor;
			await new Promise(resolve => setTimeout(resolve, delay));
			return await this._makeRequest(url, options, retries + 1);
		};

		/**
		 * Handle Auth errors, but only if auth is provided.
		 */
		if (error) {
			switch (errorType) {
				case ErrorType.AUTH:
					if (this._refreshAuth) {
						await this._refreshAuth();
						return await retry();
					} else {
						return { data, error };
					}
				case ErrorType.RETRYABLE:
					return await retry();
				case ErrorType.NETWORK:
					return await retry();
				case ErrorType.EXPECTED:
					return { data, error };
				case ErrorType.UNRECOVERABLE:
					return {
						data: null,
						error: {
							message: error.message || 'Unrecoverable error occurred',
							originalError: error,
							errorType,
						},
					};
				default:
					return {
						data: null,
						error: {
							message: 'Unexpected error occurred',
							originalError: error,
							errorType,
						},
					};
			}
		}

		return { data, error };
	}

	/**
	 * Builds a full URL from endpoint and query parameters.
	 *
	 * @private
	 * @param {string} endpoint - API endpoint path (e.g., '/users/1')
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {string} Full URL with query parameters appended
	 */
	_buildUrl(endpoint, query) {
		const url = new URL(`${this._baseUrl}${endpoint}`);
		if (query) {
			Object.keys(query).forEach(key => url.searchParams.append(key, query[key]));
		}
		return url.toString();
	}

	/**
	 * Performs a GET request.
	 *
	 * @param {string} endpoint - API endpoint path (e.g., '/users/1')
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 *
	 * @example
	 * const { data, error } = await client.get('/users/1');
	 * const { data, error } = await client.get('/users', { page: 1, limit: 10 });
	 */
	async get(endpoint, query) {
		const url = this._buildUrl(endpoint, query);

		const options = {
			method: 'GET',
		};

		return await this._makeRequest(url, options);
	}

	/**
	 * Performs a POST request.
	 *
	 * @param {string} endpoint - API endpoint path (e.g., '/users')
	 * @param {Object} data - Request body data (will be JSON stringified)
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 *
	 * @example
	 * const { data, error } = await client.post('/users', { name: 'John', email: 'john@example.com' });
	 */
	async post(endpoint, data, query) {
		const url = this._buildUrl(endpoint, query);

		const options = {
			method: 'POST',
			body: data,
		};

		return await this._makeRequest(url, options);
	}

	/**
	 * Performs a PUT request.
	 *
	 * @param {string} endpoint - API endpoint path (e.g., '/users/1')
	 * @param {Object} data - Request body data (will be JSON stringified)
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 *
	 * @example
	 * const { data, error } = await client.put('/users/1', { name: 'Jane' });
	 */
	async put(endpoint, data, query) {
		const url = this._buildUrl(endpoint, query);
		const options = {
			method: 'PUT',
			body: data,
		};

		return await this._makeRequest(url, options);
	}

	/**
	 * Performs a DELETE request.
	 *
	 * Note: This method does not support request bodies, following REST conventions
	 * where DELETE operations should not include a body. Resource identification
	 * should be done via the endpoint path and query parameters.
	 *
	 * @param {string} endpoint - API endpoint path (e.g., '/users/1')
	 * @param {Object} data - Request body data (will be JSON stringified)
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 *
	 * @example
	 * const { data, error } = await client.delete('/users/1');
	 * const { data, error } = await client.delete('/users/1', { force: true });
	 */
	async delete(endpoint, data, query) {
		const url = this._buildUrl(endpoint, query);
		const options = {
			method: 'DELETE',
			body: data,
		};
		return await this._makeRequest(url, options);
	}

	/**
	 * Performs a PATCH request.
	 *
	 * PATCH is used for partial updates to a resource. Unlike PUT which replaces
	 * the entire resource, PATCH only modifies the fields provided in the request body.
	 *
	 * @param {string} endpoint - API endpoint path (e.g., '/users/1')
	 * @param {Object} data - Partial data to update (will be JSON stringified)
	 * @param {Object} [query] - Query parameters as key-value pairs
	 * @returns {Promise<HTTPResponse>} Response with data or error
	 *
	 * @example
	 * // Update only the email field, leaving other fields unchanged
	 * const { data, error } = await client.patch('/users/1', { email: 'newemail@example.com' });
	 */
	async patch(endpoint, data, query) {
		const url = this._buildUrl(endpoint, query);
		const options = {
			method: 'PATCH',
			body: data,
		};

		return await this._makeRequest(url, options);
	}
}
